import * as React from "react";
import { loadDemoPlayer, DemoPlayer, DemoVoice } from "./demoAudio";

const BASE = process.env.PUBLIC_URL || "";

/* Generic "try now" voice demo. Real, prerendered Soniox clips (generated by
   scripts/render_experiential_demos.py and shipped as static assets).
   sequence=false: all voices at once with live faders (chorus techniques).
   sequence=true: clips play one after another (dialogue techniques). */
interface VoiceDemoProps {
  voices: DemoVoice[];
  label: string;
  sequence?: boolean;
  faders?: boolean;
}

export function VoiceDemo({ voices, label, sequence = false, faders = false }: VoiceDemoProps) {
  const [loaded, setLoaded] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [gains, setGains] = React.useState<number[]>(voices.map((v) => v.level / 100));
  const playerRef = React.useRef<DemoPlayer | null>(null);

  React.useEffect(() => () => playerRef.current?.close(), []);

  const hear = async () => {
    setErr("");
    setBusy(true);
    try {
      if (!playerRef.current) {
        playerRef.current = await loadDemoPlayer(voices);
        gains.forEach((g, i) => playerRef.current!.setGain(i, g));
      }
      if (sequence) await playerRef.current.playSequence();
      else await playerRef.current.play();
      setLoaded(true);
    } catch {
      setErr("Audio preview is being prepared.");
    } finally {
      setBusy(false);
    }
  };

  const onGain = (i: number, v: number) => {
    setGains((g) => g.map((x, j) => (j === i ? v : x)));
    playerRef.current?.setGain(i, v);
  };

  return (
    <div className="lib-ex cdemo">
      <span className="lib-ex__label">{label}</span>
      {voices.map((c, i) => (
        <div key={c.mode} className="cdemo-row">
          <div className="cdemo-row__head">
            <span className="cdemo-row__mode">{c.label}</span>
            <span className="cdemo-row__quote">“{c.line}”</span>
          </div>
          {faders && (
            <input
              className="cdemo-row__fader"
              type="range"
              min={0}
              max={1.5}
              step={0.01}
              value={gains[i]}
              disabled={!loaded}
              aria-label={`${c.label} volume`}
              onChange={(e) => onGain(i, parseFloat(e.target.value))}
            />
          )}
        </div>
      ))}
      <button type="button" className="cdemo-btn" onClick={hear} disabled={busy}>
        {busy ? "loading…" : loaded ? "▶ play again" : "▶ hear it"}
      </button>
      {err && <span className="cdemo-err">{err}</span>}
    </div>
  );
}

/* Clip manifests — must match the sets in scripts/render_experiential_demos.py. */

const EMPTY_CHAIR: DemoVoice[] = [
  { mode: "self_1", label: "The client", line: "I needed you to notice I was drowning.", level: 90, file: `${BASE}/demo/empty-chair/self_1.mp3` },
  { mode: "other", label: "The empty chair", line: "I didn't know how to say it. I'm sorry.", level: 90, file: `${BASE}/demo/empty-chair/other.mp3` },
  { mode: "self_2", label: "The client", line: "I waited so long to hear that.", level: 90, file: `${BASE}/demo/empty-chair/self_2.mp3` },
];

const PARTS: DemoVoice[] = [
  { mode: "exile", label: "Exile", line: "If they see the real me, they'll leave.", level: 90, file: `${BASE}/demo/parts-dialogue/exile.mp3` },
  { mode: "manager", label: "Manager", line: "That's why I keep everything perfect.", level: 90, file: `${BASE}/demo/parts-dialogue/manager.mp3` },
  { mode: "ifs_self", label: "Self", line: "I hear you both. Nobody has to hide.", level: 90, file: `${BASE}/demo/parts-dialogue/ifs_self.mp3` },
];

const BUS: DemoVoice[] = [
  { mode: "critic", label: "The Critic", line: "Turn back. You'll embarrass yourself.", level: 78, file: `${BASE}/demo/bus/critic.mp3` },
  { mode: "worry", label: "Worry", line: "What if it all goes wrong?", level: 62, file: `${BASE}/demo/bus/worry.mp3` },
  { mode: "anger", label: "Anger", line: "Why do we even bother?", level: 50, file: `${BASE}/demo/bus/anger.mp3` },
];

const LETTER: DemoVoice[] = [
  { mode: "letter", label: "Read back, warmly", line: "You did the best you could with what you knew then. You deserve the same kindness you give everyone else.", level: 95, file: `${BASE}/demo/compassionate-letter/letter.mp3` },
];

const NEEDED: DemoVoice[] = [
  { mode: "needed", label: "The needed words", line: "It was never your fault. I should have protected you. I'm proud of who you became.", level: 95, file: `${BASE}/demo/needed-words/needed.mp3` },
];

/* Spatial Modes: the schema-chair voices, HRTF-panned around the listener.
   Critic looming behind-left, child far left, healthy adult close right. */
const SPATIAL: DemoVoice[] = [
  { mode: "punitive_parent", label: "Punitive Parent — behind you", line: "You'll fail like you always do.", level: 80, file: `${BASE}/demo/schema-chair/punitive_parent.mp3`, position: [-1.2, 0.4, 1.8] },
  { mode: "vulnerable_child", label: "Vulnerable Child — far left", line: "I'm so tired of being scared.", level: 70, file: `${BASE}/demo/schema-chair/vulnerable_child.mp3`, position: [-3.0, -0.3, -1.2] },
  { mode: "healthy_adult", label: "Healthy Adult — close, right", line: "You're allowed to rest. I've got you.", level: 95, file: `${BASE}/demo/schema-chair/healthy_adult.mp3`, position: [0.8, 0, -0.8] },
];

export function SpatialDemo() {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const playerRef = React.useRef<DemoPlayer | null>(null);

  React.useEffect(() => () => playerRef.current?.close(), []);

  const hear = async () => {
    setErr("");
    setBusy(true);
    try {
      if (!playerRef.current) playerRef.current = await loadDemoPlayer(SPATIAL);
      await playerRef.current.play();
    } catch {
      setErr("Audio preview is being prepared.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lib-ex cdemo">
      <span className="lib-ex__label">the same voices, placed in the room — headphones on</span>
      {SPATIAL.map((c) => (
        <div key={c.mode} className="cdemo-row">
          <div className="cdemo-row__head">
            <span className="cdemo-row__mode">{c.label}</span>
            <span className="cdemo-row__quote">“{c.line}”</span>
          </div>
        </div>
      ))}
      <button type="button" className="cdemo-btn" onClick={hear} disabled={busy}>
        {busy ? "loading…" : "▶ hear it around you"}
      </button>
      {err && <span className="cdemo-err">{err}</span>}
    </div>
  );
}

/* Defusion Voice: one sticky thought, re-played manipulated until the words
   stop sounding like the truth. */
const DEFUSION: DemoVoice[] = [
  { mode: "thought", label: "The sticky thought", line: "I always ruin everything.", level: 95, file: `${BASE}/demo/defusion/thought.mp3` },
];

const DEFUSION_CHIPS: { label: string; opts: { rate?: number; muffled?: boolean } }[] = [
  { label: "▶ as spoken", opts: {} },
  { label: "▶ slowed down", opts: { rate: 0.55 } },
  { label: "▶ sped up", opts: { rate: 1.7 } },
  { label: "▶ far away", opts: { muffled: true } },
];

export function DefusionDemo() {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const playerRef = React.useRef<DemoPlayer | null>(null);

  React.useEffect(() => () => playerRef.current?.close(), []);

  const hear = async (opts: { rate?: number; muffled?: boolean }) => {
    setErr("");
    setBusy(true);
    try {
      if (!playerRef.current) playerRef.current = await loadDemoPlayer(DEFUSION);
      await playerRef.current.play(opts);
    } catch {
      setErr("Audio preview is being prepared.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lib-ex cdemo">
      <span className="lib-ex__label">“I always ruin everything.” — take its power away</span>
      <div className="vdemo-chips">
        {DEFUSION_CHIPS.map((c) => (
          <button key={c.label} type="button" className="vdemo-chip" disabled={busy} onClick={() => hear(c.opts)}>
            {c.label}
          </button>
        ))}
      </div>
      {err && <span className="cdemo-err">{err}</span>}
    </div>
  );
}

const LOOP_TAPE: DemoVoice[] = [
  { mode: "thought", label: "The loop tape", line: "What if I left the stove on, and the house burns down, and it's my fault.", level: 92, file: `${BASE}/demo/loop-tape/thought.mp3` },
];

const TAPE_FORWARD: DemoVoice[] = [
  { mode: "the_tape", label: "The tape", line: "One drink, just to take the edge off… typing the message I always regret.", level: 90, file: `${BASE}/demo/tape-forward/the_tape.mp3` },
  { mode: "the_alternative", label: "The alternative", line: "The urge passed in twenty minutes… I woke up clear on Saturday.", level: 90, file: `${BASE}/demo/tape-forward/the_alternative.mp3` },
];

const URGE: DemoVoice[] = [
  { mode: "wave", label: "The wave", line: "Just watch it swell… crest… and pass.", level: 92, file: `${BASE}/demo/urge-surfing/wave.mp3` },
];

const CYCLE: DemoVoice[] = [
  { mode: "pursuer", label: "The pursuer", line: "Talk to me — anything is better than this silence!", level: 90, file: `${BASE}/demo/couples-cycle/pursuer.mp3` },
  { mode: "withdrawer", label: "The withdrawer", line: "Underneath it… I'm scared of losing you.", level: 90, file: `${BASE}/demo/couples-cycle/withdrawer.mp3` },
];

const BRAIN: DemoVoice[] = [
  { mode: "amygdala", label: "Amygdala", line: "Intruder! Sound every alarm! Ask questions never!", level: 88, file: `${BASE}/demo/brain-cast/amygdala.mp3` },
  { mode: "cortex", label: "Cortex", line: "That was the smoke detector, not a fire.", level: 90, file: `${BASE}/demo/brain-cast/cortex.mp3` },
  { mode: "breath", label: "The breath", line: "I'll just slow everything down. In… and out.", level: 92, file: `${BASE}/demo/brain-cast/breath.mp3` },
];

const PLAYBACK: DemoVoice[] = [
  { mode: "narrator_1", label: "Narrator", line: "She reads the message from her sister — and puts the phone face-down.", level: 90, file: `${BASE}/demo/playback/narrator_1.mp3` },
  { mode: "voice", label: "A voice", line: "Five words, and I'm twelve years old again.", level: 90, file: `${BASE}/demo/playback/voice.mp3` },
  { mode: "narrator_2", label: "Narrator", line: "This time, she starts typing.", level: 90, file: `${BASE}/demo/playback/narrator_2.mp3` },
];

const TALE: DemoVoice[] = [
  { mode: "chapter_1", label: "Chapter one", line: "…a knight who polished his armor so brightly that no one — not even he — remembered his face beneath it.", level: 92, file: `${BASE}/demo/tale/chapter_1.mp3` },
  { mode: "chapter_2", label: "Chapter two", line: "…the knight unbuckled one small plate. The air touched his skin, and nothing terrible happened.", level: 92, file: `${BASE}/demo/tale/chapter_2.mp3` },
];

const INDUCTION: DemoVoice[] = [
  { mode: "path", label: "The path", line: "…no hurry at all on this path. Each step a little heavier… a little softer.", level: 92, file: `${BASE}/demo/metaphor-induction/path.mp3` },
];

const SLEEP: DemoVoice[] = [
  { mode: "story", label: "Wind-down", line: "The lake again… nothing to do now. Nowhere to be.", level: 92, file: `${BASE}/demo/sleep-story/story.mp3` },
];

export const LoopTapeDemo = () => (
  <VoiceDemo voices={LOOP_TAPE} label="on repeat, until it loses its grip" />
);
export const TapeForwardDemo = () => (
  <VoiceDemo voices={TAPE_FORWARD} sequence label="the tape, played to the end — then the other ending" />
);
export const UrgeSurfingDemo = () => (
  <VoiceDemo voices={URGE} label="ride it, don't fight it" />
);
export const CoupleCycleDemo = () => (
  <VoiceDemo voices={CYCLE} sequence label="the dance, heard from outside" />
);
export const BrainCastDemo = () => (
  <VoiceDemo voices={BRAIN} sequence label="the panic, explained from the inside" />
);
export const PlaybackDemo = () => (
  <VoiceDemo voices={PLAYBACK} sequence label="her Tuesday, performed back" />
);
export const TaleDemo = () => (
  <VoiceDemo voices={TALE} sequence label="a tale written for one listener" />
);
export const InductionDemo = () => (
  <VoiceDemo voices={INDUCTION} label="a slow personalized journey" />
);
export const SleepStoryDemo = () => (
  <VoiceDemo voices={SLEEP} label="their safe place, at bedtime" />
);

export const EmptyChairDemo = () => (
  <VoiceDemo voices={EMPTY_CHAIR} sequence label="a dialogue in turns — hear the exchange" />
);
export const PartsDialogueDemo = () => (
  <VoiceDemo voices={PARTS} sequence label="parts and Self, speaking in turn" />
);
export const BusDemo = () => (
  <VoiceDemo voices={BUS} faders label="the passengers clamor — you hold the fader" />
);
export const CompassionateLetterDemo = () => (
  <VoiceDemo voices={LETTER} label="the client's own words, read back" />
);
export const NeededWordsDemo = () => (
  <VoiceDemo voices={NEEDED} label="finally spoken, in a steady voice" />
);
